// SHA-256 hashing of the customer's email for ad-platform matching.
//
// ONLY email is hashed. Meta Advanced Matching and Google Enhanced Conversions
// both accept a pre-hashed email and use it as-is, so the raw email never enters
// the dataLayer while matching still works.
//
// Names are intentionally NOT sent: Meta's pixel expects PLAINTEXT first/last
// name and hashes them itself, so a pre-hashed name would be hashed a second
// time (a broken match) — and plaintext names in the dataLayer are exactly the
// PII exposure we're removing. Phone and address are dropped for the same
// reason (their normalization also diverges between the two platforms).
//
// Email normalization differs between the platforms, so we emit two hashes:
//   email        -> Meta rules:   trim + lowercase              (read by the Meta tag)
//   email_google -> Google rules: trim + lowercase, and for gmail/googlemail
//                                  strip a "+suffix" and all dots from the local
//                                  part                          (read by Google's
//                                                                 User-Provided Data)

const toHex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

const sha256Hex = async (value) => {
  if (!value || !window.crypto || !window.crypto.subtle) {
    return '';
  }
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(value),
  );
  return toHex(digest);
};

// Meta: trim + lowercase.
const normalizeEmailMeta = (email) => (email || '').trim().toLowerCase();

// Google: Meta normalization, plus for gmail/googlemail strip a "+suffix" and
// all dots from the local part (Google's canonical form for those domains).
const normalizeEmailGoogle = (email) => {
  const normalized = normalizeEmailMeta(email);
  const at = normalized.indexOf('@');
  if (at === -1) {
    return normalized;
  }
  let local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1);
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    local = local.split('+')[0].replace(/\./g, '');
  }
  return `${local}@${domain}`;
};

// Returns { email, email_google } as hex SHA-256 strings (or '' when the source
// is missing or Web Crypto is unavailable).
export const hashUserEmail = async (email) => {
  const [meta, google] = await Promise.all([
    sha256Hex(normalizeEmailMeta(email)),
    sha256Hex(normalizeEmailGoogle(email)),
  ]);
  return { email: meta, email_google: google };
};
